import { Stack, Typography } from '@mui/material'
import isEmpty from 'lodash/isEmpty'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { generateSupplierRoute } from '@omnea-app/fe-suppliers/details/generateSupplierRoute'
import { InfiniteScrollLoader } from '@omnea-app/fe-tables/mobile/InfiniteScrollLoader'
import { EmptyMentionList } from './components/EmptyMentionList'
import { MentionSkeletonList } from './components/MentionSkeletonList'
import { ResolveAllButton } from './components/ResolveAllButton'
import { ResolveButtons } from './components/ResolveButtons'
import { ViewOlderMentions } from './components/ViewOlderMentions'
import { getFormCommentLink, getMentionTitle } from './helpers'
import { MentionBox } from './MentionBox'
import { MentionListViewProps } from './types'

const ITEMS_PER_PAGE = 5

export const MentionListMobileView = ({
  loading,
  unresolvedMentionsCount,
  setShowResolved,
  showResolved,
  markAllMentionsResolved,
  displayedMentions,
  refetchResolvedMentions,
  displayedMentionCount,
  refetchUnresolvedMentions,
}: MentionListViewProps) => {
  const { t } = useTranslation('dashboard')

  const [visibleCount, setVisibleCount] = useState(ITEMS_PER_PAGE)

  const handleResolveChange = useCallback(
    (newResolvedValue: boolean) => {
      setShowResolved(newResolvedValue)
      setVisibleCount(ITEMS_PER_PAGE)
    },
    [setShowResolved]
  )

  const handleLoadMore = useCallback(() => {
    setVisibleCount((prev) => Math.min(prev + ITEMS_PER_PAGE, displayedMentionCount ?? 0))
  }, [displayedMentionCount])

  const hasMoreItems = visibleCount < (displayedMentionCount ?? 0)

  return (
    <Stack>
      <Stack gap={3}>
        <Typography variant="H6Semibold">{t('mentions.title')}</Typography>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <ResolveButtons
            loading={loading}
            unresolvedMentionsCount={unresolvedMentionsCount}
            handleResolveChange={handleResolveChange}
            showResolved={showResolved}
            translate={t}
          />
          <Stack>
            {!showResolved && unresolvedMentionsCount !== 0 && (
              <ResolveAllButton markAllMentionsResolved={markAllMentionsResolved} translate={t} />
            )}
          </Stack>
        </Stack>
        <Stack gap={2}>
          {loading && <MentionSkeletonList />}

          {!loading &&
            displayedMentions &&
            displayedMentions
              .slice(0, visibleCount)
              .map(
                (mention) =>
                  mention.comment && (
                    <MentionBox
                      key={mention.id}
                      id={mention.id}
                      comment={mention.comment}
                      createdAt={mention.createdAt || ''}
                      name={getMentionTitle(mention)}
                      targetUrl={getFormCommentLink(mention, generateSupplierRoute)}
                      isResolved={mention.isResolved}
                      requestId={mention.requestId}
                    />
                  )
              )}
          <InfiniteScrollLoader hasMoreItems={hasMoreItems} onReveal={handleLoadMore} />
          {displayedMentions && displayedMentionCount !== undefined && displayedMentions.length < displayedMentionCount && (
            <ViewOlderMentions
              showResolved={showResolved}
              refetchResolvedMentions={refetchResolvedMentions}
              refetchUnresolvedMentions={refetchUnresolvedMentions}
              translate={t}
            />
          )}

          {!loading && displayedMentions && isEmpty(displayedMentions) && <EmptyMentionList showResolved={showResolved} />}
        </Stack>
      </Stack>
    </Stack>
  )
}
